/**
 * UserLocationMarkerWithRadius
 * 
 * @atomic - Handles user location marker with radius circle
 * @idempotent - Pure component with React.memo
 * @modular - Self-contained user location visualization
 * 
 * Purpose: Renders user's current location with search radius circle
 * Props: User location, radius, entities (for overlap detection), user info, mode flags
 * Returns: Marker + Circle or null if hidden
 * 
 * Behavior:
 * - Shows in normal map mode (home page)
 * - Shows in immersive entity detail mode
 * - Hidden in non-immersive entity detail mode
 * - Hidden if overlaps with entity marker
 */

import React, { memo } from 'react'
import { Marker, Popup, Circle } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import { userIcon } from '../../config/mapIcons'
import { useAuth } from '../../context/AuthContext'

const UserLocationMarkerWithRadius = memo(function UserLocationMarkerWithRadius({
  userLocation,
  radius,
  entities = [],
  showUserLocation = true,
  showRadiusCircle = true,
  isEntityMode = false,
  isImmersiveMode = false
}) {
  const { t } = useTranslation()
  const { user } = useAuth()

  // Don't show if disabled or no location
  // In entity mode, only show in immersive mode (not in normal entity detail view)
  if (!showUserLocation || !userLocation) {
    return null
  }

  // Hide in entity mode unless we're in immersive mode
  if (isEntityMode && !isImmersiveMode) {
    return null
  }

  // Hide the user location marker if it overlaps with an entity marker
  const hasOverlap = entities.some(e =>
    e.currentLatitude === userLocation.latitude &&
    e.currentLongitude === userLocation.longitude
  )

  if (hasOverlap) {
    return null
  }

  return (
    <React.Fragment key="user-location">
      <Marker
        position={[userLocation.latitude, userLocation.longitude]}
        icon={userIcon}
        opacity={1.0}
      >
        <Popup 
          className="custom-popup"
          autoPan={false}
          closeButton={false}
        >
          <div className="min-w-[260px] w-full rounded-xl overflow-hidden">
            {/* Header with gradient background - matching entity popup style */}
            <div className="relative bg-gradient-to-br from-primary to-primary-700 p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0">
                  {user && user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white text-base leading-tight truncate">
                    {user ? `${user.firstName} ${user.lastName}` : t('map.yourLocation') || 'Your Location'}
                  </h3>
                </div>
              </div>
            </div>

            {/* Content - matching entity popup padding and spacing */}
            <div className="bg-white p-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-gray-700 font-medium">
                    {t('map.currentLocation') || 'Current Location'}
                  </span>
                </div>
                {radius && (
                  <div className="flex items-center gap-2 text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    <span className="text-gray-600">
                      {t('map.searchRadius') || 'Search radius'}: <span className="font-semibold text-gray-900">{radius} km</span>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Popup>
      </Marker>

      {/* Search radius circle - suppressed when showRadiusCircle=false */}
      {showRadiusCircle && (
        <Circle
          center={[userLocation.latitude, userLocation.longitude]}
          radius={radius * 1000}
          pathOptions={{ 
            color: 'rgb(73, 230, 230)', 
            fillColor: 'rgb(73, 230, 230)', 
            fillOpacity: 0.05,
            opacity: 0.3,
            weight: 1
          }}
        />
      )}
    </React.Fragment>
  )
})

export default UserLocationMarkerWithRadius
